import {
    mdiArrowRight,
    mdiCheck,
    mdiCheckAll,
    mdiCheckCircle,
    mdiInformationOutline,
    mdiPencil,
    mdiPlayOutline,
    mdiPlayCircleOutline,
    mdiSync,
    mdiUpload,
} from '@mdi/js';
import Icon from '@mdi/react';
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CoreFetch } from '../../../api';
import { ClaimApi, ProviderClaim, SupportClaim } from '../../../api/server';
import { MessagingContext } from '../../../services';
import { Variables } from '../../base';
import {
    rememberMessagingReturnForClaimEditor,
    withClaimEditorFromMessagingQuery,
} from '../claim-editor-return';
import { MessageDropZone } from '../message-drop-zone';
import { categoryMatches } from '../task-types';
import * as S from './resolve-section.styles';

export const WAITING_FOR_EMR_UPDATE = 'WAITING_FOR_EMR_UPDATE';
export const RESUME_CLAIM = 'RESUME CLAIM';
export const MOVE_TO_COLLECTIONS = 'MOVE TO COLLECTIONS';
export const UPLOADED_MEDICAL_RECORDS = 'UPLOADED_MEDICAL_RECORDS';

const RESOLVE_MARKER_BODIES = [
    WAITING_FOR_EMR_UPDATE,
    RESUME_CLAIM,
    MOVE_TO_COLLECTIONS,
    UPLOADED_MEDICAL_RECORDS,
] as const;

const RESUME_OPTION_CATEGORIES = [
    'Claim Halted',
    'Claim Denied - Rules Matched',
    'Claim Rejected - Rules Matched',
] as const;
const COLLECTIONS_OPTION_CATEGORIES = ['Inactive/Invalid Subscriber'] as const;
const RECORDS_UPLOAD_CATEGORIES = ['Missing Medical Records'] as const;

const RECORDS_UPLOAD_ACCEPT =
    '.pdf,.tif,.tiff,.jpg,.jpeg,application/pdf,image/tiff,image/jpeg,image/jpg';

export type ResolveSuccessKind = 'emr' | 'resume' | 'collections' | 'upload';

/** True when the comment body is exactly the resolve marker (not instruction text that merely mentions it). */
const commentHasMarker = (body: string | null | undefined, marker: string): boolean =>
    typeof body === 'string' && body.trim() === marker;

/** True when a comment is a system resolve response (should not appear in Conversation). */
export const isResolveMarkerComment = (body: string | null | undefined): boolean =>
    RESOLVE_MARKER_BODIES.some(marker => commentHasMarker(body, marker));

/** Which resolve path already succeeded from thread comments. */
export function getResolveSuccessKind(
    comments: { body?: string | null }[] | undefined | null,
): ResolveSuccessKind | null {
    // Prefer the most recently recorded path when both exist.
    for (let i = (comments || []).length - 1; i >= 0; i -= 1) {
        const body = comments![i]?.body;
        if (commentHasMarker(body, WAITING_FOR_EMR_UPDATE)) {
            return 'emr';
        }
        if (commentHasMarker(body, RESUME_CLAIM)) {
            return 'resume';
        }
        if (commentHasMarker(body, MOVE_TO_COLLECTIONS)) {
            return 'collections';
        }
        if (commentHasMarker(body, UPLOADED_MEDICAL_RECORDS)) {
            return 'upload';
        }
    }
    return null;
}

/** True when Resolve Success card should show. */
export function hasResolveSuccess(
    comments: { body?: string | null }[] | undefined | null,
): boolean {
    return getResolveSuccessKind(comments) !== null;
}

/** @deprecated Prefer hasResolveSuccess — kept for existing call sites. */
export function hasEmrUpdateSuccess(
    comments: { body?: string | null }[] | undefined | null,
): boolean {
    return hasResolveSuccess(comments);
}

/** True when categories include Claim Halted (manual close is blocked for these tasks). */
export function isClaimHaltedCategory(categoryNames: string[]): boolean {
    return categoryNames.some(name => name === 'Claim Halted' || name.includes('Claim Halted'));
}

/** True when message categories allow the resume/bypass Option 3 card. */
export function shouldShowResumeOption(categoryNames: string[]): boolean {
    return categoryNames.some(name =>
        RESUME_OPTION_CATEGORIES.some(target => categoryMatches(name, target)),
    );
}

/** True when message categories allow the move-to-collections Option 3 card. */
export function shouldShowCollectionsOption(categoryNames: string[]): boolean {
    return categoryNames.some(name =>
        COLLECTIONS_OPTION_CATEGORIES.some(target => categoryMatches(name, target)),
    );
}

/** True when categories call for medical-records upload resolve (no EMR / claim-editor options). */
export function shouldShowRecordsUploadOption(categoryNames: string[]): boolean {
    return categoryNames.some(name =>
        RECORDS_UPLOAD_CATEGORIES.some(target => categoryMatches(name, target)),
    );
}

const EMR_SUCCESS = {
    title: 'Marked as updated in your EMR',
    body:
        'Your EMR syncs to ENTER automatically — instantly on some systems, within 12–24 hours on others. ENTER clears the claim and resubmits once it lands.',
};

const RESUME_SUCCESS = {
    title: 'Claim resumed — check bypassed',
    body:
        'Nothing on the claim was changed. ENTER will release it as-is and bypass this check. If the payer denies, ENTER opens a denial task.',
};

const COLLECTIONS_SUCCESS = {
    title: 'Moved to collections',
    body:
        'ENTER will flip responsibility to the patient/guarantor and route this claim to collections. An invoice is generated and sent — nothing else needed from you on this task.',
};

const UPLOAD_SUCCESS = {
    title: 'Document uploaded to ENTER',
    body:
        'The document is attached to the claim and resubmission is queued — nothing else needed from you. ENTER will update this task when the payer acknowledges.',
};

const SUCCESS_COPY: Record<ResolveSuccessKind, { title: string; body: string }> = {
    emr: EMR_SUCCESS,
    resume: RESUME_SUCCESS,
    collections: COLLECTIONS_SUCCESS,
    upload: UPLOAD_SUCCESS,
};

export interface IResolveSectionProps {
    claimIds: string[];
    /** Real message id used for posting the EMR update comment */
    messageId?: string;
    /**
     * Resolved category display names (same source as CategoryPicker).
     * Prefer this over getCategoryName alone — message payloads only carry category ids.
     */
    categoryNames?: string[];
    /** Fires when the Success card is shown/hidden (so the panel can hide the bottom close link). */
    onSuccessVisibilityChange?: (visible: boolean) => void;
}

export const ResolveSection: React.FunctionComponent<IResolveSectionProps> = ({
    claimIds,
    messageId,
    categoryNames: categoryNamesProp,
    onSuccessVisibilityChange,
}) => {
    const context = useContext(MessagingContext);
    const { accountId } = useParams<{ accountId?: string }>();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [submittingEmr, setSubmittingEmr] = useState(false);
    const [submittingEmrCloseNext, setSubmittingEmrCloseNext] = useState(false);
    const [submittingResume, setSubmittingResume] = useState(false);
    const [submittingCollections, setSubmittingCollections] = useState(false);
    const [submittingUpload, setSubmittingUpload] = useState(false);
    const [localSuccessKind, setLocalSuccessKind] = useState<ResolveSuccessKind | null>(null);
    const [showClaimChooser, setShowClaimChooser] = useState(false);
    const [claimChooserSummaries, setClaimChooserSummaries] = useState<(SupportClaim | ProviderClaim)[]>([]);
    const [closing, setClosing] = useState(false);

    const commentSuccessKind = useMemo(
        () => getResolveSuccessKind(context.comments),
        [context.comments],
    );

    const categoryNamesFromContext = useMemo(() => {
        return (context.message?.links?.messageCategories || [])
            .map(cat => (cat.id ? context.getCategoryName?.(cat.id) : undefined))
            .filter((name): name is string => !!name);
    }, [context.message?.links?.messageCategories, context.getCategoryName]);

    // Parent (ChatBody) resolves names via the category catalog API; context lookup can lag or miss.
    const categoryNames = categoryNamesProp?.length ? categoryNamesProp : categoryNamesFromContext;

    const showRecordsUpload = useMemo(
        () => shouldShowRecordsUploadOption(categoryNames),
        [categoryNames],
    );
    const showCollectionsOption = useMemo(
        () => !showRecordsUpload && shouldShowCollectionsOption(categoryNames),
        [categoryNames, showRecordsUpload],
    );
    // Collections tasks also offer the bypass path, which keeps the Option 1–4 numbering stable.
    const showResumeOption = useMemo(
        () =>
            !showRecordsUpload &&
            (showCollectionsOption || shouldShowResumeOption(categoryNames)),
        [categoryNames, showRecordsUpload, showCollectionsOption],
    );
    const otherResolveBusy = submittingResume || submittingCollections || submittingEmrCloseNext;
    const emrBusy = submittingEmr || submittingEmrCloseNext;

    const successKind = localSuccessKind || commentSuccessKind;
    const showSuccess = successKind !== null;
    const isOpen = context.message?.open !== false;
    const successVisible = claimIds.length > 0 && showSuccess;
    const successCopy = successKind ? SUCCESS_COPY[successKind] : EMR_SUCCESS;

    useEffect(() => {
        setLocalSuccessKind(null);
    }, [messageId]);

    // Load account numbers for the multi-claim editor chooser.
    useEffect(() => {
        let cancelled = false;
        if (claimIds.length <= 1) {
            setClaimChooserSummaries([]);
            return;
        }

        const isSupportApp = context.account?.supportGrant === true;
        void (async () => {
            try {
                const filterIds = claimIds.map(id => `EQ:${id}`);
                const fetchedClaims = isSupportApp
                    ? await CoreFetch(ClaimApi).listSupportClaims({ filterId: filterIds })
                    : await CoreFetch(ClaimApi).listProviderClaims({ filterId: filterIds });
                if (!cancelled) {
                    setClaimChooserSummaries(fetchedClaims || []);
                }
            } catch (error) {
                console.error('Failed to load claim account numbers for editor chooser:', error);
                if (!cancelled) {
                    setClaimChooserSummaries([]);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [claimIds.join(','), context.account?.supportGrant]);

    // Before paint so ChatPanel can hide the bottom link in the same frame as Success appears.
    useLayoutEffect(() => {
        onSuccessVisibilityChange?.(successVisible);
        return () => {
            onSuccessVisibilityChange?.(false);
        };
    }, [successVisible, onSuccessVisibilityChange]);

    if (!claimIds.length) {
        return null;
    }

    const handleEmrUpdated = async () => {
        const id = messageId || context.message?.id;
        if (!id || emrBusy || showSuccess) {
            return;
        }

        setSubmittingEmr(true);
        try {
            await context.onComposeComment({
                messageId: id,
                body: WAITING_FOR_EMR_UPDATE,
                note: false,
            });
            setLocalSuccessKind('emr');
        } catch (error) {
            console.error('Failed to send WAITING_FOR_EMR_UPDATE comment:', error);
        } finally {
            setSubmittingEmr(false);
        }
    };

    const handleEmrUpdatedCloseAndNext = async () => {
        const id = messageId || context.message?.id;
        if (!id || emrBusy || closing || showSuccess || !isOpen) {
            return;
        }

        setSubmittingEmrCloseNext(true);
        try {
            await context.onComposeComment({
                messageId: id,
                body: WAITING_FOR_EMR_UPDATE,
                note: false,
            });
            if (context.onCloseAndGoToNext) {
                await context.onCloseAndGoToNext(id);
            } else {
                await context.onMarkAsClosed(id);
                await context.onMessageClose();
            }
        } catch (error) {
            console.error('Failed to mark EMR updated and close task:', error);
        } finally {
            setSubmittingEmrCloseNext(false);
        }
    };

    const handleResumeClaim = async () => {
        const id = messageId || context.message?.id;
        if (!id || submittingResume || showSuccess) {
            return;
        }

        setSubmittingResume(true);
        try {
            await context.onComposeComment({
                messageId: id,
                body: RESUME_CLAIM,
                note: false,
            });
            setLocalSuccessKind('resume');
        } catch (error) {
            console.error('Failed to send RESUME CLAIM comment:', error);
        } finally {
            setSubmittingResume(false);
        }
    };

    const handleMoveToCollections = async () => {
        const id = messageId || context.message?.id;
        if (!id || submittingCollections || showSuccess) {
            return;
        }

        setSubmittingCollections(true);
        try {
            await context.onComposeComment({
                messageId: id,
                body: MOVE_TO_COLLECTIONS,
                note: false,
            });
            setLocalSuccessKind('collections');
        } catch (error) {
            console.error('Failed to send MOVE TO COLLECTIONS comment:', error);
        } finally {
            setSubmittingCollections(false);
        }
    };

    const handleRecordsUpload = async (files: File[]) => {
        const id = messageId || context.message?.id;
        if (!id || submittingUpload || showSuccess || !files.length) {
            return;
        }

        setSubmittingUpload(true);
        try {
            await context.onComposeComment({
                messageId: id,
                body: UPLOADED_MEDICAL_RECORDS,
                note: false,
                files,
            });
            setLocalSuccessKind('upload');
        } catch (error) {
            console.error('Failed to upload medical records comment:', error);
        } finally {
            setSubmittingUpload(false);
        }
    };

    const openClaimEditor = (claimId: string) => {
        const accId = accountId || context.account?.grantedAccountId;
        if (!accId || !claimId) {
            return;
        }
        rememberMessagingReturnForClaimEditor(messageId || context.message?.id);
        const editUrl = withClaimEditorFromMessagingQuery(`/${accId}/claims/${claimId}/edit`);
        window.open(editUrl, '_blank', 'noopener,noreferrer');
        setShowClaimChooser(false);
    };

    const handleEditClaimClick = () => {
        if (claimIds.length === 1) {
            openClaimEditor(claimIds[0]);
            return;
        }
        setShowClaimChooser(open => !open);
    };

    const handleCloseAndGoToNext = async () => {
        const id = messageId || context.message?.id;
        if (!id || closing || !isOpen) {
            return;
        }

        setClosing(true);
        try {
            if (context.onCloseAndGoToNext) {
                await context.onCloseAndGoToNext(id);
            } else {
                await context.onMarkAsClosed(id);
                await context.onMessageClose();
            }
        } catch (error) {
            console.error('Failed to close task and go to next:', error);
        } finally {
            setClosing(false);
        }
    };

    if (showSuccess) {
        return (
            <S.Section>
                <S.SuccessCard>
                    <S.CardHeader>
                        <Icon
                            path={mdiCheckCircle}
                            size={0.85}
                            color={Variables.ThemeLight.SuccessConstant6}
                            style={{ flex: 'none', marginTop: 1 }}
                        />
                        <span style={{ minWidth: 0 }}>
                            <S.SuccessTitle>Success — {successCopy.title}</S.SuccessTitle>
                            <S.SuccessBody>{successCopy.body}</S.SuccessBody>
                        </span>
                    </S.CardHeader>
                    {isOpen && (
                        <S.Actions>
                            <S.PrimaryButton
                                type="button"
                                variant="unstyled"
                                onClick={handleCloseAndGoToNext}
                                disabled={closing}>
                                <span>{closing ? 'Closing…' : 'Close task & go to next'}</span>
                                <Icon path={mdiArrowRight} size={0.7} color="white" />
                            </S.PrimaryButton>
                        </S.Actions>
                    )}
                </S.SuccessCard>
            </S.Section>
        );
    }

    if (showRecordsUpload) {
        return (
            <S.Section>
                <S.SectionLabel>Resolve</S.SectionLabel>
                <S.Stack>
                    <S.Hint>
                        <Icon
                            path={mdiInformationOutline}
                            size={0.62}
                            color={Variables.ThemeLight.TextMild}
                            style={{ flex: 'none', marginTop: 1 }}
                        />
                        <span>
                            Upload the requested medical records here. Chat can explain the issue, but it
                            can&apos;t attach documents for you.
                        </span>
                    </S.Hint>

                    <S.EditCard>
                        <S.CardHeader>
                            <Icon
                                path={mdiUpload}
                                size={0.75}
                                color={Variables.ThemeLight.TextMild}
                                style={{ flex: 'none', marginTop: 1 }}
                            />
                            <span style={{ minWidth: 0 }}>
                                <S.CardTitle>Upload in ENTER</S.CardTitle>
                                <S.CardSub>
                                    Attach the document here and ENTER handles the rest
                                </S.CardSub>
                            </span>
                        </S.CardHeader>

                        <MessageDropZone
                            disabled={submittingUpload}
                            onFilesDrop={files => {
                                void handleRecordsUpload(files);
                            }}>
                            {({ isDragging, containerProps }) => (
                                <S.UploadDropZone
                                    {...containerProps}
                                    $isDragging={isDragging}
                                    $disabled={submittingUpload}
                                    role="button"
                                    tabIndex={submittingUpload ? -1 : 0}
                                    aria-disabled={submittingUpload}
                                    onClick={() => {
                                        if (!submittingUpload) {
                                            fileInputRef.current?.click();
                                        }
                                    }}
                                    onKeyDown={event => {
                                        if (submittingUpload) {
                                            return;
                                        }
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            fileInputRef.current?.click();
                                        }
                                    }}>
                                    <Icon
                                        path={mdiUpload}
                                        size={0.95}
                                        color={Variables.ThemeLight.PrimaryConstant6}
                                    />
                                    <S.UploadDropTitle>
                                        {submittingUpload ? 'Uploading…' : 'Upload records'}
                                    </S.UploadDropTitle>
                                    <S.UploadDropHint>
                                        Drag & drop, or click to browse · PDF, TIFF, JPG
                                    </S.UploadDropHint>
                                </S.UploadDropZone>
                            )}
                        </MessageDropZone>

                        <S.UploadHiddenInput
                            ref={fileInputRef}
                            type="file"
                            accept={RECORDS_UPLOAD_ACCEPT}
                            multiple
                            disabled={submittingUpload}
                            onChange={event => {
                                const files = Array.from(event.target.files || []);
                                event.target.value = '';
                                void handleRecordsUpload(files);
                            }}
                        />
                    </S.EditCard>
                </S.Stack>
            </S.Section>
        );
    }

    return (
        <S.Section>
            <S.SectionLabel>Resolve — choose one</S.SectionLabel>
            <S.Stack>
                <S.Hint>
                    <Icon
                        path={mdiInformationOutline}
                        size={0.62}
                        color={Variables.ThemeLight.TextMild}
                        style={{ flex: 'none', marginTop: 1 }}
                    />
                    <span>
                        {showResumeOption
                            ? 'These are the ways to clear this halt. Chat can explain the issue, but it can\u2019t change the claim. If nothing is changed, this claim will re-halt and send another Task.'
                            : 'These are the only two ways to clear this halt. Chat can explain the issue, but it can\u2019t change the claim. If nothing is changed, this claim will re-halt and send another Task.'}
                        {showCollectionsOption &&
                            ' Move this claim to patient/guarantor collections when insurance recovery is no longer the active path.'}
                    </span>
                </S.Hint>

                <S.EmrCard>
                    <S.CardHeader>
                        <Icon
                            path={mdiSync}
                            size={0.75}
                            color={Variables.ThemeLight.PrimaryConstant6}
                            style={{ flex: 'none', marginTop: 1 }}
                        />
                        <span style={{ minWidth: 0 }}>
                            <S.EmrCardTitle>Option 1 — Fix it in your EMR</S.EmrCardTitle>
                            <S.CardSub>
                                Correct the encounter in your EMR, then mark it below. Your EMR syncs to ENTER
                                automatically — instantly on some systems, within 12–24 hours on others.
                            </S.CardSub>
                        </span>
                    </S.CardHeader>
                    <S.Actions>
                        <S.PrimaryButton
                            type="button"
                            variant="unstyled"
                            onClick={handleEmrUpdated}
                            disabled={emrBusy || otherResolveBusy || closing}>
                            <Icon path={mdiCheck} size={0.7} color="white" />
                            <span>{submittingEmr ? 'Sending…' : "I've updated my EMR"}</span>
                        </S.PrimaryButton>
                        {isOpen && (
                            <S.OutlineButton
                                type="button"
                                variant="unstyled"
                                onClick={handleEmrUpdatedCloseAndNext}
                                disabled={emrBusy || otherResolveBusy || closing}>
                                <Icon
                                    path={mdiCheckAll}
                                    size={0.7}
                                    color={Variables.ThemeLight.TextMedium}
                                />
                                <span>
                                    {submittingEmrCloseNext ? 'Closing…' : 'Updated — close & next'}
                                </span>
                            </S.OutlineButton>
                        )}
                    </S.Actions>
                </S.EmrCard>

                <S.EditCard>
                    <S.CardHeader>
                        <Icon
                            path={mdiPencil}
                            size={0.75}
                            color={Variables.ThemeLight.TextMild}
                            style={{ flex: 'none', marginTop: 1 }}
                        />
                        <span style={{ minWidth: 0 }}>
                            <S.CardTitle>Option 2 — Edit claim in ENTER</S.CardTitle>
                            <S.CardSub>Open the claim editor to fix the missing detail and resubmit.</S.CardSub>
                        </span>
                    </S.CardHeader>
                    <S.Actions>
                        <S.PrimaryButton type="button" variant="unstyled" onClick={handleEditClaimClick}>
                            <Icon path={mdiPencil} size={0.7} color="white" />
                            <span>
                                {claimIds.length > 1 ? 'Choose claim to edit' : 'Edit claim in ENTER'}
                            </span>
                        </S.PrimaryButton>
                    </S.Actions>
                    {showClaimChooser && claimIds.length > 1 && (
                        <S.Chooser>
                            {claimIds.map(claimId => {
                                const summary = claimChooserSummaries.find(c => c.id === claimId);
                                const label = summary?.accountNumber || claimId;
                                return (
                                    <S.ChooserOption
                                        key={claimId}
                                        type="button"
                                        variant="unstyled"
                                        onClick={() => openClaimEditor(claimId)}>
                                        <S.ChooserId className="tabular" title={label}>
                                            {label}
                                        </S.ChooserId>
                                    </S.ChooserOption>
                                );
                            })}
                        </S.Chooser>
                    )}
                </S.EditCard>

                {showResumeOption && (
                    <S.ResumeCard>
                        <S.CardHeader>
                            <Icon
                                path={mdiPlayCircleOutline}
                                size={0.75}
                                color={Variables.ThemeLight.AttentionConstant6}
                                style={{ flex: 'none', marginTop: 1 }}
                            />
                            <span style={{ minWidth: 0 }}>
                                <S.ResumeCardTitle>
                                    Option 3 — Resume the claim & bypass this check
                                </S.ResumeCardTitle>
                                <S.CardSub>
                                    Nothing on the claim changes — ENTER releases it as-is and bypasses this
                                    check. Use this when you know the information is right and the check is
                                    wrong. If it denies, ENTER opens a denial task.
                                </S.CardSub>
                            </span>
                        </S.CardHeader>
                        <S.Actions>
                            <S.AttentionButton
                                type="button"
                                variant="unstyled"
                                onClick={handleResumeClaim}
                                disabled={submittingResume || emrBusy || submittingCollections}>
                                <Icon path={mdiPlayOutline} size={0.7} color={Variables.ThemeLight.AttentionInverted4} />
                                <span>
                                    {submittingResume ? 'Sending…' : 'Resume claim & bypass check'}
                                </span>
                            </S.AttentionButton>
                        </S.Actions>
                    </S.ResumeCard>
                )}

                {showCollectionsOption && (
                    <S.ResumeCard>
                        <S.CardHeader>
                            <Icon
                                path={mdiPlayCircleOutline}
                                size={0.75}
                                color={Variables.ThemeLight.AttentionConstant6}
                                style={{ flex: 'none', marginTop: 1 }}
                            />
                            <span style={{ minWidth: 0 }}>
                                <S.ResumeCardTitle>Option 4 — Move to collections</S.ResumeCardTitle>
                                <S.CardSub>
                                    Flip responsibility to the patient/guarantor and route this claim to
                                    collections. ENTER generates an invoice and sends it — use this when
                                    insurance recovery is no longer the active path.
                                </S.CardSub>
                            </span>
                        </S.CardHeader>
                        <S.Actions>
                            <S.AttentionButton
                                type="button"
                                variant="unstyled"
                                onClick={handleMoveToCollections}
                                disabled={submittingCollections || emrBusy || submittingResume}>
                                <Icon
                                    path={mdiPlayOutline}
                                    size={0.7}
                                    color={Variables.ThemeLight.AttentionInverted4}
                                />
                                <span>
                                    {submittingCollections ? 'Sending…' : 'Move to collections'}
                                </span>
                            </S.AttentionButton>
                        </S.Actions>
                    </S.ResumeCard>
                )}
            </S.Stack>
        </S.Section>
    );
};
